import {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";

import {
  parseLocalDate,
  getLocalDateString,
} from "@/lib/date";

import {
  getDateKey,
  sortByDate,
  isBirthdayToday,
  isBirthdayUpcoming,
} from "@/features/dashboard/utils";

import {
  getFamiliesWithLastVisit,
  getVisits,
  getChurches,
  getPeople,
  getChurchEventsByDistrict,
  getFieldEvents,
  getPersonalEvents,
  syncOfflineData,
} from "@/lib/api";

import { useWeeklyPlan } from "@/lib/weekly-plan";

// Module-level snapshot: survives re-renders and navigation away/back.
// Keyed by districtId so multi-district accounts get independent caches.
const _dashboardCache = new Map();

export function useDashboardData({
  districtId,
  district,
  userProfile,
}) {
  const [data, setData] = useState(() => _dashboardCache.get(districtId) || {
    families: [],
    visits: [],
    churches: [],
    people: [],
    events: [],
    personalEvents: [],
  });

  // true only on the very first load when no cache exists yet.
  // Prevents misleading "La obra avanza con calma" from flashing before real data arrives.
  const [loading, setLoading] = useState(() => !_dashboardCache.has(districtId));

  const [syncTick, setSyncTick] =
    useState(0);

  // When districtId changes at runtime (district transfer, admin switch), immediately
  // load from cache for the new district so the previous district's data is never shown
  // during the async load. The useState lazy initializer only runs once on mount.
  useEffect(() => {
    if (!districtId) return;
    const cached = _dashboardCache.get(districtId);
    if (cached) {
      setData(cached);
      setLoading(false);
    } else {
      setData({ families: [], visits: [], churches: [], people: [], events: [], personalEvents: [] });
      setLoading(true);
    }
  }, [districtId]);

  /* ============================= */
  /* SYNC EVENTS */
  /* ============================= */

  useEffect(() => {
    const handleSync = () =>
      setSyncTick((t) => t + 1);

    window.addEventListener(
      "sync-complete",
      handleSync
    );

    return () => {
      window.removeEventListener(
        "sync-complete",
        handleSync
      );
    };
  }, []);

  /* ============================= */
  /* DATES */
  /* ============================= */

  const todayStr =
    getLocalDateString();

  const today = useMemo(
    () => parseLocalDate(todayStr),
    [todayStr]
  );

  const isFutureOrToday = useCallback(
    (dateStr) => {
      const d =
        parseLocalDate(dateStr);

      return d ? d >= today : false;
    },
    [today]
  );

  const isToday = useCallback(
    (dateStr) => {
      if (!dateStr) return false;

      return (
        dateStr.slice(0, 10) ===
        todayStr
      );
    },
    [todayStr]
  );

  /* ============================= */
  /* LOAD DATA */
  /* ============================= */

  useEffect(() => {
    if (!districtId) return;

    let cancelled = false;

    const load = async () => {
      try {
        if (navigator.onLine) {
          syncOfflineData().catch(
            () => {}
          );
        }

        const [
          families,
          visits,
          churches,
          people,
          churchEvents,
          fieldEvents,
          personalEvents,
        ] = await Promise.all([
          getFamiliesWithLastVisit(
            districtId
          ).catch(() => []),

          getVisits({
            district_id: districtId,
          }).catch(() => []),

          getChurches(
            districtId
          ).catch(() => []),

          getPeople({
            district_id: districtId,
          }).catch(() => []),

          getChurchEventsByDistrict({
            district_id: districtId,
          }).catch(() => []),

          getFieldEvents({
            district_id: districtId,
          }).catch(() => []),

          getPersonalEvents({
            district_id: districtId,
          }).catch(() => []),
        ]);

        if (cancelled) return;

        const mergedEvents = [
          ...churchEvents,
          ...fieldEvents,
        ].filter((e) =>
          isFutureOrToday(
            getDateKey(e)
          )
        );

        const filteredPersonal =
          personalEvents.filter(
            (e) =>
              isFutureOrToday(
                getDateKey(e)
              ) ||
              (
                e.end_date &&
                isFutureOrToday(
                  e.end_date
                )
              )
          );

        const snapshot = {
          families,
          visits,
          churches,
          people,
          events: mergedEvents,
          personalEvents:
            filteredPersonal,
        };
        _dashboardCache.set(districtId, snapshot);
        setData(snapshot);
        setLoading(false);
      } catch {
        // silent — each sub-request already has .catch(() => [])
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    districtId,
    syncTick,
    isFutureOrToday,
  ]);

  /* ============================= */
  /* WEEKLY PLAN */
  /* ============================= */

  const { getByGetDay } =
    useWeeklyPlan();

  const todayPlan =
    getByGetDay(
      new Date().getDay()
    );

  const DAY_KEYS_SCHED = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];
  const todayScheduleKey = DAY_KEYS_SCHED[new Date().getDay()];

  /* ============================= */
  /* RAW */
  /* ============================= */

  const {
    families,
    visits,
    churches,
    people,
    events,
    personalEvents,
  } = data;

  /* ============================= */
  /* CHURCH MAP */
  /* ============================= */

  const churchMap = useMemo(
    () => Object.fromEntries(churches.map((c) => [c.id, c.name])),
    [churches]
  );

  /* ============================= */
  /* STATS */
  /* ============================= */

  const totalFamilies =
    families.length;

  const totalChurches =
    churches.length;

  const peopleCount =
    people.length;

  const visitsCount =
    visits.length;

  /* ============================= */
  /* PENDING VISITS */
  /* ============================= */

  const pendingVisits =
    useMemo(
      () =>
        visits.filter(
          (v) =>
            v.status ===
            "pendiente"
        ),
      [visits]
    );

  /* ============================= */
  /* UPCOMING */
  /* ============================= */

  const upcomingVisits =
    useMemo(() => {
      return pendingVisits
        .filter((v) =>
          isFutureOrToday(
            getDateKey(v)
          )
        )
        .sort(sortByDate)
        .slice(0, 5);
    }, [
      pendingVisits,
      isFutureOrToday,
    ]);

  const upcomingPersonalEvents =
    useMemo(() => {
      return personalEvents
        .filter(
          (e) =>
            isFutureOrToday(getDateKey(e)) ||
            (e.end_date && isFutureOrToday(e.end_date))
        )
        .sort(sortByDate)
        .slice(0, 5)
        .map((e) => ({ ...e, _source: "Personal" }));
    }, [personalEvents, isFutureOrToday]);

  const upcomingMinisterialEvents =
    useMemo(() => {
      return events
        .filter((e) =>
          isFutureOrToday(getDateKey(e)) ||
          (e.end_date && isFutureOrToday(e.end_date))
        )
        .sort(sortByDate)
        .slice(0, 5)
        .map((e) => ({ ...e, _source: e.church_id ? (churchMap[e.church_id] || "Iglesia") : "Campo" }));
    }, [events, isFutureOrToday, churchMap]);

  /* ============================= */
  /* TODAY */
  /* ============================= */

  const todayVisits =
    useMemo(() => {
      return visits.filter((v) => {
        const date =
          v.date ||
          v.visit_date;

        return (
          date &&
          isToday(date)
        );
      });
    }, [visits, isToday]);

  const todayEvents =
    useMemo(() => {
      // Helper: true for multi-day events that started before today and end today or later.
      const isOngoing = (e) => {
        const start = (e.date || e.start_date || "").slice(0, 10);
        return start && start < todayStr && e.end_date && e.end_date.slice(0, 10) >= todayStr;
      };

      return [
        ...events.map((e) => ({
          ...e,
          _source: e.church_id ? (churchMap[e.church_id] || "Iglesia") : "Campo",
        })),
        ...personalEvents.map((e) => ({
          ...e,
          _source: "Personal",
        })),
      ]
        .filter((e) => {
          const start = (e.date || e.start_date || "").slice(0, 10);
          return start && (isToday(start) || isOngoing(e));
        })
        // Ongoing events (in progress) surface before events that start today.
        .sort((a, b) => {
          const aOn = isOngoing(a) ? 0 : 1;
          const bOn = isOngoing(b) ? 0 : 1;
          return aOn - bOn;
        });
    }, [events, personalEvents, isToday, todayStr, churchMap]);

  /* ============================= */
  /* IMPORTANT DATES */
  /* Single pass over people — avoids 4×O(n) for large districts */
  /* ============================= */

  const {
    birthdaysToday,
    birthdaysUpcoming,
    anniversariesToday,
    anniversariesUpcoming,
    baptismsUpcoming,
  } = useMemo(() => {
    const bToday = [], bUpcoming = [], aToday = [], aUpcoming = [], batUpcoming = [];
    for (const p of people) {
      if (isBirthdayToday(p.birth_date ?? p.birthdate))     bToday.push(p);
      if (isBirthdayUpcoming(p.birth_date ?? p.birthdate))  bUpcoming.push(p);
      if (p.marital_status === "casado") {
        if (isBirthdayToday(p.marriage_date))    aToday.push(p);
        if (isBirthdayUpcoming(p.marriage_date)) aUpcoming.push(p);
      }
      if (p.baptism_date) {
        if (isBirthdayToday(p.baptism_date) || isBirthdayUpcoming(p.baptism_date)) batUpcoming.push(p);
      }
    }
    return {
      birthdaysToday:        bToday,
      birthdaysUpcoming:     bUpcoming,
      anniversariesToday:    aToday,
      anniversariesUpcoming: aUpcoming,
      baptismsUpcoming:      batUpcoming,
    };
  }, [people]);

  /* ============================= */
  /* COUNTS */
  /* ============================= */

  const baptismsToday = useMemo(
    () => baptismsUpcoming.filter((p) => isBirthdayToday(p.baptism_date)),
    [baptismsUpcoming]
  );

  const todayImportantCount =
    birthdaysToday.length +
    anniversariesToday.length;

  const urgentCount =
    todayVisits.length +
    todayEvents.length +
    todayImportantCount;

  /* ============================= */
  /* PERSON */
  /* ============================= */

  // Coordinators see their own name; pastors see the district pastor_name.
  const firstName = userProfile?.role === 'coordinator'
    ? (userProfile?.full_name?.split(" ")[0] ?? "")
    : (district?.pastor_name?.split(" ")[0] ?? "");

  /* ============================= */
  /* RETURN */
  /* ============================= */

  return {
    loading,

    // raw data
    families,
    people,
    visits,

    // stats
    totalFamilies,
    totalChurches,
    peopleCount,
    visitsCount,

    // today
    todayPlan,
    todayScheduleKey,
    todayVisits,
    todayEvents,
    todayImportantCount,
    urgentCount,

    // upcoming
    upcomingVisits,
    upcomingPersonalEvents,
    upcomingMinisterialEvents,

    // important dates
    birthdaysToday,
    birthdaysUpcoming,
    anniversariesUpcoming,
    baptismsToday,
    baptismsUpcoming,

    // derived
    firstName,
  };
}